// Which websites may embed a company's visualizer. Shared between the admin
// panel (normalizes what the owner types) and Pages Functions (builds the
// Content-Security-Policy frame-ancestors directive).

/** Any website may embed it. */
export const ANY_ORIGIN = '*';

const ORIGIN = /^https?:\/\/(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*(:\d{1,5})?$/;

/** True for a stored entry: "*", "https://example.com", "https://*.example.com", "http://localhost:3000". */
export function isOrigin(value: string): boolean {
  return value === ANY_ORIGIN || ORIGIN.test(value);
}

/**
 * Turns what someone types ("Example.com", "https://www.example.com/page",
 * "*.example.com") into a stored entry, or null if it isn't a website.
 * Without a scheme, https is assumed.
 */
export function normalizeOrigin(input: string): string | null {
  const raw = input.trim().toLowerCase();
  if (raw === ANY_ORIGIN) return ANY_ORIGIN;
  const m = /^(?:(https?):\/\/)?([^/?#]+)/.exec(raw);
  if (!m) return null;
  const host = m[2].replace(/\.$/, '');
  const scheme = m[1] ?? (/^(localhost|127\.0\.0\.1)(:\d+)?$/.test(host) ? 'http' : 'https');
  const origin = `${scheme}://${host}`;
  return ORIGIN.test(origin) ? origin : null;
}

/** "https://example.com" -> "https://www.example.com" and back, so both are easy to allow; null for others. */
export function wwwTwin(origin: string): string | null {
  const m = /^(https?:\/\/)(www\.)?([^*:]+\.[a-z]+)$/.exec(origin);
  if (!m || /^\d+(\.\d+)*$/.test(m[3])) return null;
  return m[2] ? `${m[1]}${m[3]}` : `${m[1]}www.${m[3]}`;
}

/** frame-ancestors sources: the app itself (admin preview) plus the allowed sites, or any site. */
export function frameAncestors(origins: string[]): string {
  if (origins.includes(ANY_ORIGIN)) return '*';
  return ["'self'", ...origins.filter((o) => ORIGIN.test(o))].join(' ');
}
